import React, { useState, useEffect, useMemo, useRef } from "react";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Edit";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import LinkIcon from "@material-ui/icons/Link";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import SaveIcon from "@material-ui/icons/Save";
import SearchIcon from "@material-ui/icons/Search";
import firebase from "firebase/app";
import "firebase/database";
import Cotizacion from "./Cotizacion";

const mxn = (n) =>
    "$" +
    Number(n).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtFecha = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso + "T12:00:00");
    return d.toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" });
};

const ListaCotizaciones = () => {
    const [cotizaciones, setCotizaciones] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [busqueda, setBusqueda] = useState("");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [cotizacionSeleccionada, setCotizacionSeleccionada] = useState(null);
    const [guardando, setGuardando] = useState(false);
    const [ordenCol, setOrdenCol] = useState("createdAt");
    const [ordenDir, setOrdenDir] = useState("desc");
    const cotizacionRef = useRef(null);

    // ── Escuchar Firebase en tiempo real ─────────────────────────
    useEffect(() => {
        const ref = firebase.database().ref("cotizaciones");
        ref.on("value", (snap) => {
            const val = snap.val();
            if (!val) {
                setCotizaciones([]);
                setCargando(false);
                return;
            }
            const lista = Object.entries(val)
                .map(([id, data]) => ({ id, ...data }));
            setCotizaciones(lista);
            setCargando(false);
        });
        return () => ref.off();
    }, []);

    // ── Búsqueda + ordenamiento ───────────────────────────────────
    const filtradas = useMemo(() => {
        const q = busqueda.toLowerCase().trim();
        let lista = q
            ? cotizaciones.filter((c) =>
                [c.cliente, c.evento, c.lugar, c.fecha, c.eventoId]
                    .filter(Boolean)
                    .some((field) => field.toLowerCase().includes(q))
            )
            : [...cotizaciones];

        lista.sort((a, b) => {
            let va, vb;
            if (ordenCol === "fecha") {
                va = a.fecha || "";
                vb = b.fecha || "";
            } else if (ordenCol === "cliente") {
                va = (a.cliente || "").toLowerCase();
                vb = (b.cliente || "").toLowerCase();
            } else if (ordenCol === "evento") {
                va = (a.evento || "").toLowerCase();
                vb = (b.evento || "").toLowerCase();
            } else {
                // createdAt por defecto
                va = a.createdAt || 0;
                vb = b.createdAt || 0;
            }
            if (va < vb) return ordenDir === "asc" ? -1 : 1;
            if (va > vb) return ordenDir === "asc" ? 1 : -1;
            return 0;
        });

        return lista;
    }, [cotizaciones, busqueda, ordenCol, ordenDir]);

    const toggleOrden = (col) => {
        if (ordenCol === col) {
            setOrdenDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setOrdenCol(col);
            setOrdenDir("asc");
        }
    };

    const SortIcon = ({ col }) => {
        if (ordenCol !== col) return null;
        return ordenDir === "asc"
            ? <ArrowUpwardIcon style={{ fontSize: 14, marginLeft: 4, verticalAlign: "middle" }} />
            : <ArrowDownwardIcon style={{ fontSize: 14, marginLeft: 4, verticalAlign: "middle" }} />;
    };

    // ── Handlers ──────────────────────────────────────────────────
    const abrirNueva = () => {
        setCotizacionSeleccionada(null);
        setDialogOpen(true);
    };

    const abrirEditar = (cot) => {
        setCotizacionSeleccionada(cot.id);
        setDialogOpen(true);
    };

    const cerrarDialog = () => {
        setDialogOpen(false);
        setCotizacionSeleccionada(null);
    };

    const handleGuardar = async () => {
        if (!cotizacionRef.current) return;
        setGuardando(true);
        await cotizacionRef.current.guardar();
        setGuardando(false);
    };

    const handleGenerarPDF = () => {
        if (cotizacionRef.current) cotizacionRef.current.generarPDF();
    };

    const onGuardado = (id) => {
        if (!cotizacionSeleccionada) {
            // Es nueva: cerrar y abrir en modo edición con el id recién creado
            setCotizacionSeleccionada(id);
        }
        // Si es edición: no cerrar, el usuario cierra manualmente
    };

    const duplicar = async (cot) => {
        const { id, createdAt, updatedAt, ...datos } = cot;
        const nuevoCodigo = (() => {
            const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
            return Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
        })();
        const nueva = {
            ...datos,
            cliente: `${datos.cliente || ""} (copia)`,
            eventoId: null,
            numeroCot: nuevoCodigo,
            createdAt: firebase.database.ServerValue.TIMESTAMP,
            updatedAt: firebase.database.ServerValue.TIMESTAMP,
        };
        try {
            await firebase.database().ref("cotizaciones").push(nueva);
        } catch (err) {
            console.error("Error al duplicar:", err);
        }
    };

    // ── Render ────────────────────────────────────────────────────
    return (
        <Box>
            {/* Toolbar */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" style={{ gap: 12 }}>
                <Typography variant="h6" style={{ fontWeight: 600 }}>
                    Cotizaciones
                </Typography>
                <Box display="flex" style={{ gap: 12 }} alignItems="center" flexWrap="wrap">
                    <TextField
                        placeholder="Buscar por cliente, evento, lugar, fecha…"
                        variant="outlined"
                        size="small"
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        style={{ minWidth: 280 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon style={{ fontSize: 18, color: "#aaa" }} />
                                </InputAdornment>
                            ),
                            endAdornment: busqueda ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setBusqueda("")}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={abrirNueva}
                    >
                        Nueva cotización
                    </Button>
                </Box>
            </Box>

            {/* Tabla */}
            {cargando ? (
                <Box display="flex" justifyContent="center" py={6}>
                    <CircularProgress />
                </Box>
            ) : filtradas.length === 0 ? (
                <Paper variant="outlined" style={{ padding: 40, textAlign: "center" }}>
                    <Typography color="textSecondary">
                        {busqueda ? "Sin resultados para esa búsqueda." : "Aún no hay cotizaciones registradas."}
                    </Typography>
                </Paper>
            ) : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow style={{ backgroundColor: "#f5f5f5" }}>
                                <TableCell style={{ fontWeight: 600, whiteSpace: "nowrap" }}>N° Cot.</TableCell>
                                <TableCell
                                    style={{ fontWeight: 600, cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
                                    onClick={() => toggleOrden("createdAt")}
                                >
                                    Creado <SortIcon col="createdAt" />
                                </TableCell>
                                <TableCell
                                    style={{ fontWeight: 600, cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
                                    onClick={() => toggleOrden("fecha")}
                                >
                                    Fecha evento <SortIcon col="fecha" />
                                </TableCell>
                                <TableCell
                                    style={{ fontWeight: 600, cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
                                    onClick={() => toggleOrden("cliente")}
                                >
                                    Cliente <SortIcon col="cliente" />
                                </TableCell>
                                <TableCell
                                    style={{ fontWeight: 600, cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
                                    onClick={() => toggleOrden("evento")}
                                >
                                    Evento <SortIcon col="evento" />
                                </TableCell>
                                <TableCell style={{ fontWeight: 600 }}>Lugar</TableCell>
                                <TableCell style={{ fontWeight: 600 }}>Participantes</TableCell>
                                <TableCell style={{ fontWeight: 600 }} align="right">Total</TableCell>
                                <TableCell style={{ fontWeight: 600 }} align="center">Vinculado</TableCell>
                                <TableCell style={{ fontWeight: 600 }} align="center">Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtradas.map((cot) => (
                                <TableRow key={cot.id} hover>
                                    <TableCell style={{ whiteSpace: "nowrap", fontSize: 13, fontFamily: "monospace", letterSpacing: 1 }}>
                                        {cot.numeroCot || <span style={{ color: "#bbb" }}>—</span>}
                                    </TableCell>
                                    <TableCell style={{ whiteSpace: "nowrap", fontSize: 13 }}>
                                        {cot.createdAt ? fmtFecha(new Date(cot.createdAt).toISOString().slice(0, 10)) : <span style={{ color: "#bbb" }}>—</span>}
                                    </TableCell>
                                    <TableCell style={{ whiteSpace: "nowrap", fontSize: 13 }}>
                                        {fmtFecha(cot.fecha)}
                                    </TableCell>
                                    <TableCell style={{ fontSize: 13 }}>
                                        {cot.cliente || <span style={{ color: "#bbb" }}>—</span>}
                                    </TableCell>
                                    <TableCell style={{ fontSize: 13 }}>
                                        {cot.evento || <span style={{ color: "#bbb" }}>—</span>}
                                    </TableCell>
                                    <TableCell style={{ fontSize: 13 }}>
                                        {cot.lugar || <span style={{ color: "#bbb" }}>—</span>}
                                    </TableCell>
                                    <TableCell style={{ fontSize: 13 }} align="center">
                                        {cot.participantes || "—"}
                                    </TableCell>
                                    <TableCell style={{ fontSize: 13, fontWeight: 600 }} align="right">
                                        {cot.total ? mxn(cot.total) : "—"}
                                    </TableCell>
                                    <TableCell align="center">
                                        {cot.eventoId ? (
                                            <Tooltip title={`Evento: ${cot.eventoId}`}>
                                                <Chip
                                                    icon={<LinkIcon style={{ fontSize: 14 }} />}
                                                    label={cot.eventoId}
                                                    size="small"
                                                    variant="outlined"
                                                    color="primary"
                                                    style={{ fontSize: 11, maxWidth: 120 }}
                                                />
                                            </Tooltip>
                                        ) : (
                                            <span style={{ color: "#ccc", fontSize: 13 }}>—</span>
                                        )}
                                    </TableCell>
                                    <TableCell align="center" style={{ whiteSpace: "nowrap" }}>
                                        <Tooltip title="Editar">
                                            <IconButton size="small" onClick={() => abrirEditar(cot)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Duplicar">
                                            <IconButton size="small" onClick={() => duplicar(cot)} style={{ marginLeft: 4 }}>
                                                <FileCopyIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Contador */}
            {!cargando && cotizaciones.length > 0 && (
                <Box mt={1}>
                    <Typography variant="caption" color="textSecondary">
                        {filtradas.length} de {cotizaciones.length} cotizaciones
                    </Typography>
                </Box>
            )}

            {/* Dialog editor */}
            <Dialog
                open={dialogOpen}
                onClose={cerrarDialog}
                fullWidth
                maxWidth="md"
                scroll="paper"
            >
                <DialogTitle disableTypography>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="h6" style={{ fontWeight: 600 }}>
                            {cotizacionSeleccionada ? "Editar cotización" : "Nueva cotización"}
                        </Typography>
                        <IconButton onClick={cerrarDialog} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Divider style={{ marginTop: 8 }} />
                </DialogTitle>

                <DialogContent style={{ paddingTop: 16 }}>
                    <Cotizacion
                        ref={cotizacionRef}
                        cotizacionId={cotizacionSeleccionada}
                        onGuardado={onGuardado}
                        hideActions
                    />
                </DialogContent>

                <Divider />
                <DialogActions style={{ padding: "12px 24px", gap: 8 }}>
                    <Button onClick={cerrarDialog} variant="outlined" size="large">
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        size="large"
                        startIcon={<PictureAsPdfIcon />}
                        onClick={handleGenerarPDF}
                    >
                        Generar PDF
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        startIcon={guardando ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                        onClick={handleGuardar}
                        disabled={guardando}
                    >
                        {cotizacionSeleccionada ? "Actualizar" : "Guardar cotización"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ListaCotizaciones;